/**
 * Reliable Image Generation Service
 * Ensures consistent image generation across all game screens
 */

import { supabaseClient } from './supabaseClient';

export interface ImageGenerationRequest {
  prompt: string;
  type: 'character' | 'scene' | 'background' | 'item' | 'portrait';
  width?: number;
  height?: number;
  style?: string;
  fallbackEnabled?: boolean;
}

export interface ImageGenerationResult {
  url: string;
  cached: boolean;
  fallback: boolean;
  source: 'pollinations' | 'together' | 'huggingface' | 'cache';
}

class ReliableImageService {
  private supabase = supabaseClient;
  private cache = new Map<string, string>();
  private generationQueue = new Map<string, Promise<ImageGenerationResult>>();
  
  private readonly FALLBACK_IMAGES = {
    character: 'https://image.pollinations.ai/prompt/cyberpunk%20character%20portrait%20futuristic?width=512&height=512&seed=12345&nologo=true',
    scene: 'https://image.pollinations.ai/prompt/cyberpunk%20city%20neon%20lights%20rain?width=1024&height=512&seed=12345&nologo=true',
    background: 'https://image.pollinations.ai/prompt/dark%20cyberpunk%20background%20texture?width=1920&height=1080&seed=12345&nologo=true',
    item: 'https://image.pollinations.ai/prompt/cyberpunk%20tech%20gadget%20futuristic?width=256&height=256&seed=12345&nologo=true',
    portrait: 'https://image.pollinations.ai/prompt/cyberpunk%20avatar%20digital%20art?width=256&height=256&seed=12345&nologo=true'
  };

  /**
   * Generate image with multiple fallback strategies
   */
  async generateImage(request: ImageGenerationRequest): Promise<ImageGenerationResult> {
    const cacheKey = this.getCacheKey(request);
    
    // Check cache first
    if (this.cache.has(cacheKey)) {
      return {
        url: this.cache.get(cacheKey)!,
        cached: true,
        fallback: false,
        source: 'cache'
      };
    }

    // Check if already generating
    if (this.generationQueue.has(cacheKey)) {
      return await this.generationQueue.get(cacheKey)!;
    }

    // Start new generation
    const generationPromise = this.performGeneration(request);
    this.generationQueue.set(cacheKey, generationPromise);

    try {
      const result = await generationPromise;
      this.cache.set(cacheKey, result.url);
      return result;
    } finally {
      this.generationQueue.delete(cacheKey);
    }
  }

  private async performGeneration(request: ImageGenerationRequest): Promise<ImageGenerationResult> {
    const { prompt, type, width = 1024, height = 1024, fallbackEnabled = true } = request;
    
    // Strategy 1: Try Pollinations (most reliable)
    try {
      const url = await this.generateWithPollinations(prompt, width, height);
      if (await this.isImageAccessible(url)) {
        return { url, cached: false, fallback: false, source: 'pollinations' };
      }
    } catch (error) {
      console.warn('[ReliableImage] Pollinations failed:', error);
    }

    // Strategy 2: Try Together AI
    try {
      const url = await this.generateWithTogether(prompt);
      if (await this.isImageAccessible(url)) {
        return { url, cached: false, fallback: false, source: 'together' };
      }
    } catch (error) {
      console.warn('[ReliableImage] Together AI failed:', error);
    }

    // Strategy 3: Try HuggingFace
    try {
      const url = await this.generateWithHuggingFace(prompt);
      if (await this.isImageAccessible(url)) {
        return { url, cached: false, fallback: false, source: 'huggingface' };
      }
    } catch (error) {
      console.warn('[ReliableImage] HuggingFace failed:', error);
    }

    // Strategy 4: Use fallback image
    if (fallbackEnabled) {
      const fallbackUrl = this.getFallbackImage(type, prompt);
      return { url: fallbackUrl, cached: false, fallback: true, source: 'pollinations' };
    }

    throw new Error('All image generation strategies failed');
  }

  private async generateWithPollinations(prompt: string, width: number, height: number): Promise<string> {
    const seed = Math.floor(Math.random() * 100000);
    const encodedPrompt = encodeURIComponent(prompt);
    return `https://image.pollinations.ai/prompt/${encodedPrompt}?width=${width}&height=${height}&seed=${seed}&nologo=true&enhance=true`;
  }

  private async generateWithTogether(prompt: string): Promise<string> {
    const apiKey = import.meta.env.VITE_TOGETHER_AI_KEY;
    if (!apiKey) throw new Error('Together AI key not configured');

    const response = await fetch('https://api.together.xyz/v1/images/generations', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${apiKey}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        model: 'black-forest-labs/FLUX.1-schnell-Free',
        prompt: `${prompt}, digital art, high quality, cyberpunk style`,
        width: 1024,
        height: 1024,
        steps: 4,
        n: 1
      })
    });

    if (!response.ok) {
      throw new Error(`Together API error: ${response.status}`);
    }

    const data = await response.json();
    return data.data[0]?.url || '';
  }

  private async generateWithHuggingFace(prompt: string): Promise<string> {
    const token = import.meta.env.HUGGING_FACE_HUB_TOKEN;
    if (!token) throw new Error('HuggingFace token not configured');

    const response = await fetch(
      "https://api-inference.huggingface.co/models/black-forest-labs/FLUX.1-dev",
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        method: "POST",
        body: JSON.stringify({ inputs: prompt }),
      }
    );

    if (!response.ok) {
      throw new Error(`HuggingFace API error: ${response.status}`);
    }

    const blob = await response.blob();
    return URL.createObjectURL(blob);
  }

  private getFallbackImage(type: ImageGenerationRequest['type'], prompt: string): string {
    const baseUrl = this.FALLBACK_IMAGES[type];
    const seed = this.hashString(prompt) % 100000;
    return baseUrl.replace(/seed=\d+/, `seed=${seed}`);
  }

  private async isImageAccessible(url: string): Promise<boolean> {
    try {
      const response = await fetch(url, { method: 'HEAD' });
      return response.ok;
    } catch {
      return false;
    }
  }

  private getCacheKey(request: ImageGenerationRequest): string {
    return `${request.type}_${request.width}_${request.height}_${this.hashString(request.prompt)}`;
  }

  private hashString(str: string): number {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash;
    }
    return Math.abs(hash);
  }

  /**
   * Preload images for common scenarios
   */
  async preloadCommonImages(): Promise<void> {
    const commonScenarios = [
      { prompt: 'cyberpunk login screen background neon', type: 'background' as const },
      { prompt: 'cyberpunk character selection screen', type: 'background' as const },
      { prompt: 'cyberpunk story scene urban night', type: 'scene' as const },
      { prompt: 'cyberpunk combat arena futuristic', type: 'scene' as const },
      { prompt: 'cyberpunk trading market digital', type: 'scene' as const }
    ];

    await Promise.allSettled(
      commonScenarios.map(scenario => this.generateImage(scenario))
    );
  }

  /**
   * Generate image for specific game screens
   */
  async generateForScreen(screenType: 'login' | 'character-select' | 'story' | 'combat' | 'trading', context?: string): Promise<string> {
    const prompts = {
      login: 'cyberpunk city login screen background with neon lights and rain, dark atmospheric, futuristic buildings',
      'character-select': 'cyberpunk character selection interface, holographic displays, digital avatars, futuristic UI',
      story: context ? `cyberpunk scene: ${context}, atmospheric, neon lights, urban decay` : 'cyberpunk city street scene at night, neon signs, rain, atmospheric',
      combat: 'cyberpunk combat arena, futuristic weapons, digital battlefield, high-tech environment',
      trading: 'cyberpunk marketplace, digital commerce, holographic displays, trading interface'
    };

    const result = await this.generateImage({
      prompt: prompts[screenType],
      type: screenType === 'login' || screenType === 'character-select' ? 'background' : 'scene',
      width: screenType === 'login' ? 1920 : 1024,
      height: screenType === 'login' ? 1080 : 1024
    });

    return result.url;
  }

  /**
   * Clear cache and reset service
   */
  clearCache(): void {
    this.cache.clear();
    this.generationQueue.clear();
  }
}

export const reliableImageService = new ReliableImageService();